import {ErrorResponseCode} from '../error';
import {Coverage} from './coverage';
import {compareDurations, Duration, durationToNumber, isWholeLife} from './duration';
import {JobRiskClass} from './job-risk-class';
import {arePersonInsurancesEqual, PersonInsurance, Rider, RiderType} from './person-insurance';

export interface Protection {
  personInsurances: PersonInsurance[];
}

export function areProtectionsEqual(protection1: Protection, protection2: Protection): boolean {
  if (protection1.personInsurances.length !== protection2.personInsurances.length) {
    return false;
  }

  const sortedInsurances1 = [...protection1.personInsurances].sort((a, b) =>
    compareDurations(a.duration, b.duration),
  );
  const sortedInsurances2 = [...protection2.personInsurances].sort((a, b) =>
    compareDurations(a.duration, b.duration),
  );

  return sortedInsurances1.every((insurance1, index) => {
    const insurance2 = sortedInsurances2[index];
    return arePersonInsurancesEqual(insurance1, insurance2);
  });
}

export function getMonthlyPrice(insurances: Pick<PersonInsurance, 'price'>[]): number {
  return Number(insurances.reduce((acc, insurances) => acc + insurances.price, 0).toFixed(2));
}

export function getAnnualPrice(insurances: Pick<PersonInsurance, 'annualPrice'>[]): number {
  return Number(insurances.reduce((acc, insurances) => acc + insurances.annualPrice, 0).toFixed(2));
}

export function getTotalCoverage(insurances: Pick<PersonInsurance, 'coverage'>[]): number {
  return insurances.reduce((acc, insurances) => acc + insurances.coverage, 0);
}

export function getRiders(insurances: PersonInsurance[]): Rider[] {
  return insurances.reduce(
    (acc, insurances) => (insurances.riders ? [...acc, ...insurances.riders] : acc),
    [] as Rider[],
  );
}

export function extractCoverageFromProtection(protection: Protection): Coverage {
  const coverage: Coverage = {lifeInsurances: {}};
  protection.personInsurances.forEach((pi) => {
    coverage.lifeInsurances[pi.duration] = pi.coverage;
    if (pi.riders && pi.riders.length > 0) {
      coverage.riders = coverage.riders ? [...coverage.riders, ...pi.riders] : [...pi.riders];
    }
  });
  return coverage;
}

export function getFormattedSurchargeRate(surchargeRate: number): number {
  return (surchargeRate + 1) * 100;
}

export function priceToRevenueRatioTooHigh(monthlyPrice: number, revenue: number): boolean {
  return monthlyPrice / (revenue / 12) >= 0.01;
}

export function getDisabilityRiderMaxCoverage(
  totalLifeInsuranceCoverage: number,
  jobRiskClass: JobRiskClass,
): number {
  let maxCoverage = 0.015 * totalLifeInsuranceCoverage;
  switch (jobRiskClass) {
    case JobRiskClass['4A']:
    case JobRiskClass['3A']: {
      maxCoverage = Math.min(maxCoverage, 3_000);
      break;
    }
    case JobRiskClass['2A']:
    case JobRiskClass['1A']: {
      maxCoverage = Math.min(maxCoverage, 2_000);
      break;
    }
    default: {
      maxCoverage = Math.min(maxCoverage, 1_000);
    }
  }

  return maxCoverage;
}

export function getDisabilityRiderDurations(jobRiskClass: JobRiskClass): number[] {
  return [JobRiskClass['4A'], JobRiskClass['3A']].includes(jobRiskClass) ? [2, 5] : [2];
}

export enum PossibleProtection {
  TERM_LIFE = 'TERM_LIFE',
  WHOLE_LIFE = 'WHOLE_LIFE',
  CHILD_ENDORSEMENT = 'CHILD_ENDORSEMENT',
}

export function deconstructProtectionMonthlyPrices(protection: Protection): {[key in Duration]?: number} {
  const deconstructedMonthlyPrices: {[key in Duration | RiderType]?: number} = {};

  protection.personInsurances.forEach((pi) => {
    let totalRiderPrice = 0;

    pi.riders?.forEach((rider) => {
      const riderPrice = rider.price ?? 0;
      totalRiderPrice += riderPrice;
      deconstructedMonthlyPrices[rider.type] = Number(riderPrice.toFixed(2));
    });

    const insurancePrice = pi.price - totalRiderPrice;
    deconstructedMonthlyPrices[pi.duration] = Number(insurancePrice.toFixed(2));
  });

  return deconstructedMonthlyPrices;
}

export function removeRiderFromCoverageBasedOnErrorCodesInPlace(
  codes: ErrorResponseCode[],
  coverage: Coverage,
): void {
  codes.forEach((c: ErrorResponseCode) => {
    switch (c) {
      case ErrorResponseCode.CANCER_RIDER_NOT_ALLOWED: {
        coverage.riders = coverage.riders?.filter((r) => r.type !== RiderType.CANCER);
        break;
      }
      case ErrorResponseCode.DISABILITY_RIDER_NOT_ALLOWED: {
        coverage.riders = coverage.riders?.filter((r) => r.type !== RiderType.DISABILITY);
        break;
      }
    }
  });
}

export function getValidTermLifeDurations(
  durations: Duration[],
  age: number,
  maxEligibleAge: number,
): Duration[] {
  return durations.filter((duration) => {
    if (isWholeLife(duration)) {
      throw new Error(`Invalid duration: ${duration} is whole life`);
    }
    return age + durationToNumber(duration) <= maxEligibleAge + 1; // We want to allow the user to be insured up to the maximum eligible age, plus one year
  });
}
